import {
  CalendarOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  SendOutlined,
  SettingOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Row,
  Select,
  Spin,
  Switch,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../UserContext';
import { BaseComponentProps } from '../../types';
import { MuteScheduleCard } from './MuteScheduleCard';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const timezoneOptions = [
  { value: 'America/New_York', label: 'America/New_York' },
  { value: 'America/Chicago', label: 'America/Chicago' },
  { value: 'America/Denver', label: 'America/Denver' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
  { value: 'America/Anchorage', label: 'America/Anchorage' },
  { value: 'America/Phoenix', label: 'America/Phoenix' },
  { value: 'Pacific/Honolulu', label: 'Pacific/Honolulu' },
];

const mapTimezoneValueToLabel = (value: string): string => {
  const option = timezoneOptions.find(option => option.value === value);
  return option ? option.label : value;
};

const mapTimezoneLabelToValue = (label: string): string => {
  const option = timezoneOptions.find(option => option.label === label);
  return option ? option.value : label;
};

type CalendarOAuthRow = {
  completed: boolean;
  credentials_present: boolean;
};

type CalendarEventSourceRow = {
  source_id: string;
  provider: string;
  display_name: string;
  sync_from_email?: string;
  last_sync_time?: string;
  error_count?: number;
  is_active?: boolean;
};

const CALENDAR_DISCONNECT_PROVIDERS = ['google', 'azure'] as const;
type CalendarDisconnectProvider =
  (typeof CALENDAR_DISCONNECT_PROVIDERS)[number];

function calendarProviderDisplayName(
  provider: CalendarDisconnectProvider,
): string {
  return provider === 'google' ? 'Google Calendar' : 'Microsoft 365 (Outlook)';
}

function isGoogleOrAzureCalendarKey(key: string): boolean {
  const lower = key.toLowerCase();
  return lower === 'google' || lower === 'azure';
}

function CalendarOAuthBadges({ status }: { status: CalendarOAuthRow }) {
  return (
    <>
      <Badge
        status={status.completed ? 'success' : 'error'}
        text={`Completed: ${status.completed ? 'Yes' : 'No'}`}
      />
      <br />
      <Badge
        status={status.credentials_present ? 'success' : 'error'}
        text={`Credentials: ${status.credentials_present ? 'Yes' : 'No'}`}
      />
    </>
  );
}

/** True if OAuth, push subscription, or an active event source suggests this provider is in use. */
function isCalendarProviderIntegrated(
  provider: CalendarDisconnectProvider,
  status: Record<string, unknown>,
): boolean {
  const oauth = (status.oauth_status ?? {}) as Record<string, CalendarOAuthRow>;
  const o = oauth[provider];
  if (o?.credentials_present || o?.completed) {
    return true;
  }
  const subs = (status.subscription_status ?? {}) as Record<
    string,
    { active?: boolean }
  >;
  if (subs[provider]?.active) {
    return true;
  }
  const sources = Array.isArray(status.event_sources)
    ? (status.event_sources as CalendarEventSourceRow[])
    : [];
  return sources.some(
    s => String(s.provider).toLowerCase() === provider && Boolean(s.is_active),
  );
}

export const AgentInfo = ({
  agent,
  apiUrl,
  clientConfig,
}: BaseComponentProps): JSX.Element => {
  const user = useContext(UserContext);
  const [agentInfo, setAgentInfo] = useState<Record<string, unknown> | null>(
    null,
  );
  const [calendarIntegrationStatus, setCalendarIntegrationStatus] =
    useState<Record<string, unknown> | null>(null);
  const [agentProperties, setAgentProperties] = useState<{
    is_brokerage_admin: boolean;
    is_team_lead: boolean;
  } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [propertiesLoading, setPropertiesLoading] = useState<boolean>(false);
  const [disconnectingProvider, setDisconnectingProvider] =
    useState<CalendarDisconnectProvider | null>(null);
  const [onboardingUpdating, setOnboardingUpdating] = useState<boolean>(false);
  const [onboardingStage, setOnboardingStage] = useState<string>('');
  const [form] = Form.useForm();

  const getCalendarIntegrationStatus = async (): Promise<
    Record<string, unknown>
  > => {
    if (!user?.accessToken) {
      throw new Error('Not authenticated');
    }
    const integrationRes = await fetch(
      `${apiUrl}/api/calendar/integration-status?agent_id=${agent.id}`,
      {
        headers: { Authorization: `Bearer ${user.accessToken}` },
      },
    );
    if (!integrationRes.ok) {
      throw new Error('Failed to fetch calendar integration status');
    }
    return integrationRes.json() as Promise<Record<string, unknown>>;
  };

  const fetchAgentInfo = async () => {
    try {
      setLoading(true);
      if (!user?.accessToken) {
        return;
      }

      const [agentRes, integrationData] = await Promise.all([
        fetch(`${apiUrl}/api/agent/info/${agent.id}`, {
          headers: { Authorization: `Bearer ${user.accessToken}` },
        }),
        getCalendarIntegrationStatus(),
      ]);

      if (!agentRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const agentData = await agentRes.json();

      if (agentData.personalization?.mute_sms_until) {
        agentData.personalization.mute_sms_until = dayjs
          .utc(agentData.personalization.mute_sms_until)
          .tz(agentData.personalization.timezone || 'UTC');
      }

      if (agentData.personalization) {
        const personalization = { ...agentData.personalization };

        // Handle None values for boolean fields - set defaults
        if (personalization.show_only_lead_events === null) {
          personalization.show_only_lead_events = true; // Default: filter to leads
        }

        personalization.timezone = mapTimezoneValueToLabel(
          personalization.timezone,
        );
        form.setFieldsValue(personalization);
      } else {
        form.setFieldsValue({});
      }
      setAgentInfo(agentData);
      setOnboardingStage(
        (agentData.onboarding_flow as { stage?: string } | null)?.stage ?? '',
      );
      setCalendarIntegrationStatus(integrationData);
      setAgentProperties(
        agentData.agent_properties || {
          is_brokerage_admin: false,
          is_team_lead: false,
        },
      );
    } catch (err) {
      // console.error('Fetch error:', err);
      void message.error('Failed to load agent or calendar status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchAgentInfo();
  }, [agent]);

  const handleCalendarDisconnect = async (
    provider: CalendarDisconnectProvider,
  ) => {
    try {
      setDisconnectingProvider(provider);
      const res = await fetch(
        `${apiUrl}/api/agent/${agent.id}/calendar/disconnect?provider=${provider}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${user?.accessToken}` },
        },
      );
      const body = (await res.json()) as {
        success?: boolean;
        error?: string;
      };
      if (res.ok && body.success) {
        void message.success(
          `${calendarProviderDisplayName(provider)} disconnected`,
        );
        setCalendarIntegrationStatus(await getCalendarIntegrationStatus());
      } else {
        void message.error(body.error ?? 'Failed to disconnect calendar');
      }
    } catch {
      void message.error('Failed to disconnect calendar');
    } finally {
      setDisconnectingProvider(null);
    }
  };

  const handleSave = async (values: Record<string, unknown>) => {
    try {
      setLoading(true);
      const payload = { ...values };
      if (payload.mute_sms_until) {
        const tz = mapTimezoneLabelToValue(String(payload.timezone) || 'UTC');
        payload.mute_sms_until = (payload.mute_sms_until as dayjs.Dayjs)
          .tz(tz, true)
          .utc()
          .toISOString();
      } else {
        payload.mute_sms_until = '';
      }

      const res = await fetch(
        `${apiUrl}/api/agent/personalization/${agent.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user?.accessToken}`,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        throw new Error();
      }
      void message.success('Personalization updated successfully');
      await fetchAgentInfo();
    } catch {
      void message.error('Failed to update personalization');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSync = async (values: Record<string, unknown>) => {
    try {
      setLoading(true);
      const res = await fetch(`${apiUrl}/api/calendar/bulk-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.accessToken}`,
        },
        body: JSON.stringify({
          agent_id: agent.id,
          sync_type: values.sync_type || 'date_range',
          provider: values.provider || null,
          start_date:
            Array.isArray(values.date_range) && values.date_range[0]
              ? values.date_range[0].toISOString()
              : null,
          end_date:
            Array.isArray(values.date_range) && values.date_range[1]
              ? values.date_range[1].toISOString()
              : null,
        }),
      });

      const result = await res.json();
      if (result.success) {
        void message.success('Bulk sync triggered successfully');
      } else {
        void message.error(result.message || 'Failed to trigger bulk sync');
      }
    } catch {
      void message.error('Failed to trigger bulk sync');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitation = async (values: Record<string, unknown>) => {
    try {
      setLoading(true);
      const res = await fetch(`${apiUrl}/api/calendar/send-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.accessToken}`,
        },
        body: JSON.stringify({
          agent_id: agent.id,
          custom_message: values.custom_message || '',
          force_send: values.force_send || false,
        }),
      });

      const result = await res.json();
      if (result.success) {
        void message.success('Calendar invitation sent successfully');
      } else {
        void message.error(result.message || 'Failed to send invitation');
      }
    } catch {
      void message.error('Failed to send calendar invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAgentPropertyChange = async (
    field: 'is_brokerage_admin' | 'is_team_lead',
    value: boolean,
  ) => {
    try {
      setPropertiesLoading(true);
      const res = await fetch(`${apiUrl}/api/agent/properties/${agent.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.accessToken}`,
        },
        body: JSON.stringify({ [field]: value }),
      });

      const result = await res.json();
      if (result.success) {
        setAgentProperties(prev => ({
          is_brokerage_admin: prev?.is_brokerage_admin ?? false,
          is_team_lead: prev?.is_team_lead ?? false,
          [field]: value,
        }));
        void message.success('Agent property updated');
      } else {
        void message.error(result.message || 'Failed to update agent property');
      }
    } catch {
      void message.error('Failed to update agent property');
    } finally {
      setPropertiesLoading(false);
    }
  };

  const handleUpdateOnboardingFlow = async (stage: string) => {
    const flow = agentInfo?.onboarding_flow as
      | { id?: string; stage?: string }
      | null
      | undefined;
    if (!flow?.id) {
      void message.error('No onboarding flow found');
      return;
    }
    const previousStage = onboardingStage;
    try {
      setOnboardingUpdating(true);
      setOnboardingStage(stage);
      const res = await fetch(`${apiUrl}/api/onboarding/flow/${flow.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.accessToken}`,
        },
        body: JSON.stringify({ stage }),
      });
      if (!res.ok) {
        throw new Error();
      }
      void message.success('Onboarding stage updated');
      await fetchAgentInfo();
    } catch {
      setOnboardingStage(previousStage);
      void message.error('Failed to update onboarding stage');
    } finally {
      setOnboardingUpdating(false);
    }
  };

  if (loading || !agentInfo) {
    return <Spin spinning={loading} style={{ width: '100%' }} />;
  }

  const {
    personalization,
    onboarding_flow,
    phone,
    dialer_phone,
    phone_override,
    is_hw_enabled,
    hw_enabled,
    hw_disabled,
    hw_internal_notes,
    twilio_conversation_id,
    created_at,
    _created_at,
    updated_at,
    has_calander_setup,
  } = agentInfo;

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: 'auto' }}>
      <Row gutter={[24, 24]}>
        {/* General Info */}
        <Col md={12} xs={24}>
          <Card
            bodyStyle={{ padding: 20 }}
            bordered
            hoverable
            style={{
              boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)',
              borderRadius: 12,
            }}
            title={
              <>
                <InfoCircleOutlined
                  style={{ color: '#1890ff', marginRight: 8 }}
                />
                General Info
              </>
            }
          >
            <Descriptions bordered column={1} size="middle">
              <Descriptions.Item label="Phone">
                {String(phone) || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Dialer Number">
                {String(dialer_phone) || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone Override">
                {String(phone_override) || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="HW Enabled">
                {String(is_hw_enabled) ? 'Yes' : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="HW Enabled At">
                {String(hw_enabled) || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="HW Disabled At">
                {String(hw_disabled) || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="HW Notes">
                {String(hw_internal_notes) || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Twilio Conv. ID">
                {String(twilio_conversation_id) || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Created At (Client)">
                {String(created_at) || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Created At (System)">
                {String(_created_at) || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {String(updated_at) || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Calendar Setup">
                {has_calander_setup ? '✅ Yes' : '❌ No'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Personalization */}
        <Col md={12} xs={24}>
          <Card
            bodyStyle={{ padding: 0, opacity: personalization ? 1 : 0.5 }}
            bordered
            hoverable
            style={{
              boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)',
              borderRadius: 12,
            }}
            title={
              <>
                <UserOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                Personalization
              </>
            }
          >
            {!personalization && (
              <Alert
                message="Personalization is not available until onboarding is initialized for this agent."
                showIcon
                style={{ margin: 16 }}
                type="info"
              />
            )}
            <Collapse bordered={false} defaultActiveKey={['1']}>
              <Panel header="Edit Personalization Settings" key="1">
                <Form
                  disabled={!personalization}
                  form={form}
                  initialValues={
                    (personalization as Record<string, unknown>) || {}
                  }
                  layout="vertical"
                  onFinish={values => void handleSave(values)}
                  style={{ padding: 16 }}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Nickname" name="nickname">
                        <Input placeholder="Agent's Nickname" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Assistant Name" name="assistant_name">
                        <Input placeholder="Assistant's Name" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Sync Time" name="sync_time">
                        <Input placeholder="e.g. 10:00 AM" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Auxiliary Sync Time"
                        name="auxiliary_sync_time"
                      >
                        <Input placeholder="Optional" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Business Hours Start"
                        name="business_hours_start"
                      >
                        <Input placeholder="07:00 (default)" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Business Hours End"
                        name="business_hours_end"
                      >
                        <Input placeholder="19:00 (default)" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item label="Timezone" name="timezone">
                    <Select
                      options={timezoneOptions}
                      placeholder="Select timezone"
                    />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Appointment Duration (minutes)"
                        name="default_appointment_duration_minutes"
                      >
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Use Times for Tasks"
                        name="use_times_for_tasks"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Opt Out of Text Messages"
                        name="text_opted_out"
                        tooltip="When enabled, the agent will not send text messages"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Show Only Lead Events"
                        name="show_only_lead_events"
                        tooltip="When enabled, only show calendar events related to leads. When disabled, show all calendar events."
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item label="Mute SMS Until" name="mute_sms_until">
                    <DatePicker showTime style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item>
                    <Button block htmlType="submit" type="primary">
                      Save Changes
                    </Button>
                  </Form.Item>
                </Form>
              </Panel>
            </Collapse>
          </Card>
        </Col>
      </Row>

      {/* Agent Properties */}
      <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
        <Col md={12} xs={24}>
          <Card
            bodyStyle={{ padding: 20 }}
            bordered
            hoverable
            style={{
              boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)',
              borderRadius: 12,
            }}
            title={
              <>
                <SettingOutlined style={{ color: '#eb2f96', marginRight: 8 }} />
                Agent Properties
              </>
            }
          >
            <Spin spinning={propertiesLoading}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <Switch
                      checked={agentProperties?.is_brokerage_admin ?? false}
                      onChange={checked =>
                        void handleAgentPropertyChange(
                          'is_brokerage_admin',
                          checked,
                        )
                      }
                    />
                    <Text>Brokerage Admin</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <Switch
                      checked={agentProperties?.is_team_lead ?? false}
                      onChange={checked =>
                        void handleAgentPropertyChange('is_team_lead', checked)
                      }
                    />
                    <Text>Team Lead</Text>
                  </div>
                </Col>
              </Row>
            </Spin>
          </Card>
        </Col>
        <Col md={12} xs={24}>
          <MuteScheduleCard
            agent={agent}
            apiUrl={apiUrl}
            clientConfig={clientConfig}
          />
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
        {/* Bulk Calendar Sync */}
        <Col md={12} xs={24}>
          <Card
            bodyStyle={{ padding: 0 }}
            bordered
            hoverable
            style={{
              boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)',
              borderRadius: 12,
            }}
            title={
              <>
                <SyncOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                Bulk Calendar Sync
              </>
            }
          >
            <Collapse bordered={false} defaultActiveKey={['1']}>
              <Panel header="Trigger Bulk Sync" key="1">
                <Form
                  layout="vertical"
                  onFinish={values => void handleBulkSync(values)}
                  style={{ padding: 16 }}
                >
                  <Form.Item label="Provider" name="provider">
                    <Select
                      options={[
                        { value: 'google', label: 'Google' },
                        { value: 'azure', label: 'Azure' },
                      ]}
                      placeholder="Select provider"
                    />
                  </Form.Item>
                  <Form.Item label="Date Range" name="date_range">
                    <RangePicker showTime style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item>
                    <Button block htmlType="submit" type="primary">
                      Trigger Bulk Sync
                    </Button>
                  </Form.Item>
                </Form>
              </Panel>
            </Collapse>
          </Card>
        </Col>

        {/* Send Calendar Invitation */}
        <Col md={12} xs={24}>
          <Card
            bodyStyle={{ padding: 0 }}
            bordered
            hoverable
            style={{
              boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)',
              borderRadius: 12,
            }}
            title={
              <>
                <SendOutlined style={{ color: '#722ed1', marginRight: 8 }} />
                Send Calendar Invitation
              </>
            }
          >
            <Collapse bordered={false} defaultActiveKey={['1']}>
              <Panel header="Send Invitation" key="1">
                <Form
                  layout="vertical"
                  onFinish={values => void handleSendInvitation(values)}
                  style={{ padding: 16 }}
                >
                  <Form.Item label="Custom Message" name="custom_message">
                    <Input.TextArea
                      placeholder="Optional message to include"
                      rows={4}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Force Send"
                    name="force_send"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item>
                    <Button block htmlType="submit" type="primary">
                      Send Invitation
                    </Button>
                  </Form.Item>
                </Form>
              </Panel>
            </Collapse>
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Onboarding Status */}
      <Card
        bodyStyle={{ padding: 20 }}
        bordered
        hoverable
        style={{ boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)', borderRadius: 12 }}
        title={
          <>
            <CalendarOutlined style={{ color: '#13c2c2', marginRight: 8 }} />
            Onboarding Status
          </>
        }
      >
        {onboarding_flow ? (
          <>
            <Descriptions bordered column={1} size="middle">
              <Descriptions.Item label="Stage">
                {(onboarding_flow as { stage?: string }).stage || ''}
              </Descriptions.Item>
              <Descriptions.Item label="Complete">
                {(onboarding_flow as { is_complete?: boolean }).is_complete ? (
                  <Text type="success">
                    <CheckCircleOutlined /> Yes
                  </Text>
                ) : (
                  <Text type="secondary">No</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Greeting Message">
                {(onboarding_flow as { greeting_message?: string })
                  .greeting_message || ''}
              </Descriptions.Item>
              <Descriptions.Item label="Greeting Sent At">
                {(onboarding_flow as { greeting_sent_at?: string })
                  .greeting_sent_at || ''}
              </Descriptions.Item>
              <Descriptions.Item label="Sequence Number">
                {(onboarding_flow as { sequence_number?: string | number })
                  .sequence_number || ''}
              </Descriptions.Item>
            </Descriptions>
            <Divider />
            <Spin spinning={onboardingUpdating}>
              <Row align="middle" gutter={[16, 16]}>
                <Col>
                  <Text strong>Update Stage:</Text>
                </Col>
                <Col>
                  <Select
                    onChange={(val: string) =>
                      void handleUpdateOnboardingFlow(val)
                    }
                    options={[
                      { value: 'first', label: 'first' },
                      {
                        value: 'calendar_sync_pending',
                        label: 'calendar_sync_pending',
                      },
                      { value: 'failed', label: 'failed' },
                    ]}
                    style={{ width: 220 }}
                    value={onboardingStage}
                  />
                </Col>
                <Col>
                  <Popconfirm
                    cancelText="Cancel"
                    description="This sets stage to 'complete' and marks is_complete = true."
                    okText="Mark Complete"
                    onConfirm={() =>
                      void handleUpdateOnboardingFlow('complete')
                    }
                    title="Mark onboarding as complete?"
                  >
                    <Button
                      disabled={
                        !!(onboarding_flow as { is_complete?: boolean })
                          .is_complete
                      }
                      icon={<CheckCircleOutlined />}
                      type="primary"
                    >
                      Mark Complete
                    </Button>
                  </Popconfirm>
                </Col>
              </Row>
            </Spin>
          </>
        ) : (
          <Text type="secondary">No onboarding data available.</Text>
        )}
      </Card>

      {calendarIntegrationStatus && (
        <>
          <Divider />
          <Card
            bodyStyle={{ padding: 0 }}
            bordered
            hoverable
            style={{
              boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)',
              borderRadius: 12,
            }}
            title={
              <>
                <CalendarOutlined
                  style={{ color: '#1890ff', marginRight: 8 }}
                />
                Calendar Integration Status
              </>
            }
          >
            <Collapse
              bordered={false}
              defaultActiveKey={['oauth', 'subscriptions', 'sources']}
            >
              <Panel header="OAuth Status" key="oauth">
                {(() => {
                  const oauthMap = (calendarIntegrationStatus.oauth_status ??
                    {}) as Record<string, CalendarOAuthRow>;
                  return (
                    <Descriptions bordered column={1} size="small">
                      {CALENDAR_DISCONNECT_PROVIDERS.map(providerLower => {
                        const raw =
                          oauthMap[providerLower] ??
                          oauthMap[providerLower.toUpperCase()];
                        const status: CalendarOAuthRow = raw ?? {
                          completed: false,
                          credentials_present: false,
                        };
                        const canDisconnect = isCalendarProviderIntegrated(
                          providerLower,
                          calendarIntegrationStatus,
                        );
                        return (
                          <Descriptions.Item
                            key={providerLower}
                            label={providerLower.toUpperCase()}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 8,
                              }}
                            >
                              <div>
                                <CalendarOAuthBadges status={status} />
                              </div>
                              {canDisconnect && (
                                <Popconfirm
                                  cancelText="Cancel"
                                  description={`Stops sync and removes stored tokens for ${calendarProviderDisplayName(providerLower)}.`}
                                  okButtonProps={{ danger: true }}
                                  okText="Disconnect"
                                  onConfirm={() =>
                                    void handleCalendarDisconnect(providerLower)
                                  }
                                  title={`Disconnect ${calendarProviderDisplayName(providerLower)}?`}
                                >
                                  <Button
                                    danger
                                    loading={
                                      disconnectingProvider === providerLower
                                    }
                                    size="small"
                                    type="default"
                                  >
                                    Disconnect
                                  </Button>
                                </Popconfirm>
                              )}
                            </div>
                          </Descriptions.Item>
                        );
                      })}
                      {Object.entries(oauthMap)
                        .filter(
                          ([providerKey]) =>
                            !isGoogleOrAzureCalendarKey(providerKey),
                        )
                        .map(([providerKey, status]) => (
                          <Descriptions.Item
                            key={providerKey}
                            label={String(providerKey).toUpperCase()}
                          >
                            <CalendarOAuthBadges status={status} />
                          </Descriptions.Item>
                        ))}
                    </Descriptions>
                  );
                })()}
              </Panel>
              <Panel header="Subscription Status" key="subscriptions">
                <Descriptions bordered column={1} size="small">
                  {Object.entries(
                    (calendarIntegrationStatus.subscription_status ??
                      {}) as Record<
                      string,
                      {
                        active: boolean;
                        expiration_date: string;
                        channel_id: string;
                      }
                    >,
                  ).map(([provider, sub]) => (
                    <Descriptions.Item
                      key={provider}
                      label={String(provider).toUpperCase()}
                    >
                      <Badge
                        status={sub.active ? 'success' : 'error'}
                        text={`Active: ${sub.active ? 'Yes' : 'No'}`}
                      />{' '}
                      <br />
                      Expiration:{' '}
                      {sub.expiration_date
                        ? dayjs(sub.expiration_date).format('YYYY-MM-DD HH:mm')
                        : 'N/A'}{' '}
                      <br />
                      Channel ID: {sub.channel_id}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </Panel>
              <Panel header="Event Sources" key="sources">
                {(Array.isArray(calendarIntegrationStatus.event_sources)
                  ? calendarIntegrationStatus.event_sources
                  : []
                ).map((source: CalendarEventSourceRow) => (
                  <Card
                    key={source.source_id}
                    style={{ marginBottom: 16 }}
                    title={`${String(source.provider).toUpperCase()}: ${source.display_name}`}
                    type="inner"
                  >
                    <p>
                      <Text strong>Source ID:</Text> {source.source_id}
                    </p>
                    <p>
                      <Text strong>Calendar Email:</Text>{' '}
                      {source.sync_from_email || 'N/A'}
                    </p>
                    <p>
                      <Text strong>Last Sync:</Text>{' '}
                      {source.last_sync_time
                        ? dayjs(source.last_sync_time).format(
                            'YYYY-MM-DD HH:mm',
                          )
                        : 'N/A'}
                    </p>
                    <p>
                      <Text strong>Errors:</Text> {source.error_count}
                    </p>
                    <p>
                      <Text strong>Active:</Text>{' '}
                      {source.is_active ? 'Yes ✅' : 'No ❌'}
                    </p>
                  </Card>
                ))}
              </Panel>
            </Collapse>
          </Card>
        </>
      )}
    </div>
  );
};
